/**
 * useFilteredAndSortedOptions — the results selection layer, extracted verbatim
 * from SgpFlowScreen.jsx (god-file decomposition).
 *
 * Order of operations (unchanged): ghost-card filter, stale-game filter,
 * unreliable-books filter, date-keyword filter, query-relevance filter,
 * text search, chip filters (sportsbook/market/player/leg/result-type),
 * deep-link filter, +EV filter, min-books threshold, My Books hard filter,
 * line-type filter, then the sort switch.
 *
 * All component state arrives via the single `d` params object; the memo's
 * dependency array is IDENTICAL to the original, so recompute timing and
 * closure-staleness semantics are preserved exactly.
 */

import { useMemo } from 'react';
import { resolvedRelevanceTerms } from '../utils/resolvedRelevanceTerms';
import { getSgpLegMarketLabels, optionMatchesMarketFilters, optionMatchesSportsbookFilters, optionPassesLineTypeFilter, countLineTypeSplits, optionMatchesPlayerFilters } from '../utils/sgpFlowUtils';
import { getCanonicalSortEv, cleanPerBookEv, buildMarketPeersMap, isLayBookName } from '../utils/canonicalEv';
import { scoreMyBooksOverlap, sortByMyBooksPriority, normBookName } from '../../utils/resultSorting';
import { cardGroupKey } from '../utils/cardGrouping';
import { dedupeCardBooks, lineBookOdds, canonicalBookName } from '../utils/cardBooks';
import { getHideNoEvRows, useHideNoEvRows, effectiveMinBooksStraights, effectiveMinBooksSGP, effectiveMinMyBooks } from '../utils/searchDisplay';

/**
 * The results filter + sort pipeline as a pure function of its inputs. The
 * results page runs it through useFilteredAndSortedOptions; the Background
 * Queries card runs it with a fresh search's default filters so its counts match
 * the page's All Books / My Books toggle (utils/resultCounts.js).
 */
export function computeFilteredAndSortedOptions(d) {
    // site_config/search_display.hideNoEvRows (utils/searchDisplay.js): off =
    // show everything, the automatic min-books defaults become "All".
    const _hideNoEv = typeof d?.hideNoEvRows === 'boolean' ? d.hideNoEvRows : getHideNoEvRows();
    const {
        // results pools + query context
        fetchedOptions, myBooksFetchedOptions, resultsContextQuery, responseMetadata,
        referenceTime, didBroaden, preferredSportsbook,
        // filter state
        searchText, filterSportsbooks, filterMarkets, filterPlayers, filterResultType,
        filterDeepLinksOnly, filterPositiveEvOnly, filterLineType, filterLegCount,
        disableMinBooksFilter, disableMyBooksPriority, disableDateKeywordFilter, disableRelevanceFilter,
        // sort / books mode
        sortBy, _effectiveBooksSort, _wantsMyBooksMode, _myBooksList,
        // profile + consensus context
        userProfile, consensusOptions,
        // component-scope helpers (recreated per render, same as pre-extraction)
        _getMyBooksBest, _isOptionPlayerProp, _sortEvForBook,
        getMarketRelevanceToQuery, normalizeQuery,
    } = d;

{
    const rq = resultsContextQuery;
    // Use server's frozen reference time when in archive/freeze mode so that
    // archived games aren't dropped by "game already started / ended" filters.
    const _nowMs = referenceTime ? new Date(referenceTime).getTime() : Date.now();
    const _stageCounts = [];
    const _markStage = (name, arr) => { _stageCounts.push({ name, count: arr.length }); };
    // When My Books mode is active AND the backend returned an independent My Books result
    // set (myBooksFetchedOptions), use that as the source. Those results are pre-sorted by
    // ev_percent_mybooks and contain bets that rank highly on the user's books specifically —
    // they are NOT a subset of the All Books top-500. This gives the user a genuine
    // independent "My Books search" rather than just filtering the All Books pool.
    //
    // Fallback to fetchedOptions when myBooksFetchedOptions is empty (e.g. for live
    // fixture queries where the backend doesn't compute a separate My Books set).
    const _useMyBooksSet = (_wantsMyBooksMode || sortBy === 'mybooks') && myBooksFetchedOptions.length > 0;
    let filtered = _useMyBooksSet ? [...myBooksFetchedOptions] : [...fetchedOptions];
    _markStage('input', filtered);

    // ── Ghost card filter: reject options with empty bookOdds.
    // v1.14.13 — Previously kept `_noOddsYet` placeholders as a fallback when the
    // list would otherwise be empty. In practice that produced exactly the bad UX
    // users complained about (empty "— / Time TBD / 0 sportsbooks / No Link
    // Available" cards for fixtures with no parseable date). The creation-time
    // filter upstream now only lets through placeholders with a real title AND a
    // parseable future date, so any `_noOddsYet` that reaches here is renderable.
    // Keep the "drop malformed rows with empty bookOdds" behaviour for non-
    // placeholder cards so genuinely-broken rows are never shown.
    filtered = filtered.filter(option => {
        if (option?._noOddsYet) {
            // Require a title + a parseable, non-past date as a final guard even
            // if an upstream path created this card with weaker checks.
            const hasTitle = !!(option?.title || (option?.raw?.home_team && option?.raw?.away_team));
            if (!hasTitle) return false;
            const rawDate = option?.date || option?.raw?.start_date || option?.raw?.commence_time || option?.raw?.event_date;
            let dateMs = null;
            if (rawDate != null && rawDate !== '') {
                if (typeof rawDate === 'number') dateMs = rawDate > 1e12 ? rawDate : rawDate * 1000;
                else if (typeof rawDate === 'object') {
                    if (typeof rawDate._seconds === 'number') dateMs = rawDate._seconds * 1000;
                    else if (typeof rawDate.seconds === 'number') dateMs = rawDate.seconds * 1000;
                    else if (typeof rawDate.toMillis === 'function') { try { dateMs = rawDate.toMillis(); } catch { /* ignore */ } }
                } else {
                    const ms = new Date(rawDate).getTime();
                    if (Number.isFinite(ms)) dateMs = ms;
                }
            }
            if (dateMs == null) return false;
            if (dateMs < _nowMs - 30 * 60 * 1000) return false;
            return true;
        }
        // Cached SGP defense-in-depth — ticker clicks flow through this path and
        // can sometimes surface SGPs where all four pricing tiers in the builder
        // above came up empty (backend returned sgps whose all_books / bookOdds
        // / best_book / combined_price were all missing). Those render as
        // "— / Time TBD / 0 Sportsbooks / No Link Available" ghost cards. The
        // builder now skips them at creation time, but this guards against any
        // older cached payload or alternate code path that still produces one.
        if (option?._isCachedSGP) {
            // Require at least one priced book entry OR a finite combined_price.
            // Previously we accepted any bookOdds with non-zero key count, which
            // let through payloads shaped like { SomeBook: null } or { SomeBook:
            // {} } — UnifiedBetCard then dropped the null entries and rendered
            // the ghost card the ticker flow was producing. This matches the
            // priced-book check used for non-SGP cards below.
            const bo = option?.bookOdds || option?.raw?.bookOdds || {};
            const _hasPricedBook = Object.values(bo).some(v => {
                if (v == null) return false;
                if (typeof v === 'number') return Number.isFinite(v);
                if (typeof v === 'object') {
                    const p = v.price;
                    return Number.isFinite(typeof p === 'number' ? p : Number(p));
                }
                return false;
            });
            const _cp = option?.combined_price ?? option?.raw?.combined_price;
            const _cpNum = typeof _cp === 'number' ? _cp : Number(_cp);
            const _hasPrice = Number.isFinite(_cpNum) && _cpNum !== 0;
            if (!_hasPricedBook && !_hasPrice) return false;
            // SGP card also needs a parseable start_date to avoid "Time TBD".
            // Same reader the card uses: raw.start_date first, then option.start_date.
            const rawDate = option?.raw?.start_date || option?.start_date || option?.date;
            if (!rawDate) return false;
            return true;
        }
        // ── Generic renderability guard for non-SGP, non-placeholder cards.
        //
        // Two patterns of ghost cards have been seen in production:
        //   1. bookOdds has KEYS but every VALUE is null/undefined/price-less —
        //      `UnifiedBetCard`'s own filter drops the null entries and lands
        //      on 0 books with nothing to price.
        //   2. `BackgroundQueryContext.transformedResults` stamps every item
        //      with `book: o.sportsbook || 'N/A'`, so items the backend sent
        //      without a sportsbook arrive here with the literal string
        //      `'N/A'`. The earlier filter's `_fallbackBook` check treated
        //      that as truthy and let the card through, producing the
        //      "— / Time TBD / 0 Sportsbooks / No Link Available" cards
        //      the ticker flow was showing (v1.15.x).
        //
        // New rule: an option is renderable only if AT LEAST ONE of these is true:
        //   (a) bookOdds has at least one entry with a finite numeric price, OR
        //   (b) `availableBooks` has at least one non-placeholder entry AND the
        //       option carries a finite top-level price.
        //   (c) A top-level { sportsbook|book, price } fallback exists AND the
        //       sportsbook name isn't a known placeholder ('N/A', 'TBD', etc.).
        // Either way the card will show a real book + a real price — never a ghost.
        const _isPlaceholderBook = (name) => {
            if (!name) return true;
            const s = String(name).trim().toLowerCase();
            return s === '' || s === 'n/a' || s === 'na' || s === 'tbd' ||
                   s === 'unknown' || s === 'theoretical' || s === 'none';
        };
        const bo = option?.bookOdds || option?.raw?.bookOdds || {};
        const _pricedBookKeys = Object.entries(bo).filter(([k, v]) => {
            if (_isPlaceholderBook(k)) return false;
            if (v == null) return false;
            if (typeof v === 'number') return Number.isFinite(v);
            if (typeof v === 'object') {
                const p = v.price;
                return Number.isFinite(typeof p === 'number' ? p : Number(p));
            }
            return false;
        });
        if (_pricedBookKeys.length > 0) return true;
        // availableBooks path: trust only if the array has at least one non-placeholder
        const _ab = option?.availableBooks || option?.raw?.availableBooks || [];
        const _abHasReal = Array.isArray(_ab) && _ab.some(b => !_isPlaceholderBook(b));
        const _fallbackBookRaw =
            option?.sportsbook || option?.book ||
            option?.raw?.sportsbook || option?.raw?.book || null;
        const _fallbackBook = _isPlaceholderBook(_fallbackBookRaw) ? null : _fallbackBookRaw;
        const _fallbackPriceRaw = option?.price ?? option?.raw?.price ?? null;
        const _fallbackPrice = typeof _fallbackPriceRaw === 'number'
            ? _fallbackPriceRaw
            : Number(_fallbackPriceRaw);
        const _hasFinitePrice = Number.isFinite(_fallbackPrice) && _fallbackPrice !== 0;
        if (_abHasReal && _hasFinitePrice) return true;
        if (_fallbackBook && _hasFinitePrice) return true;
        // ── Ghost drop: log a breadcrumb so we can diagnose why the backend
        // is producing price-less payloads. Throttled by a module-level
        // counter so a huge payload doesn't flood the console.
        try {
            if (typeof window !== 'undefined') {
                window.__ghostDropStats = window.__ghostDropStats || { count: 0, samples: [] };
                window.__ghostDropStats.count += 1;
                if (window.__ghostDropStats.samples.length < 5) {
                    window.__ghostDropStats.samples.push({
                        market: option?.raw?.market || option?.title || null,
                        selection: option?.raw?.selection || option?.selection || null,
                        sportsbook: option?.raw?.sportsbook || option?.sportsbook || null,
                        book: option?.book || null,
                        price: option?.raw?.price ?? option?.price ?? null,
                        bookOddsKeys: Object.keys(bo),
                        availableBooks: _ab,
                        id: option?.id || option?.raw?.id || null,
                    });
                }
            }
        } catch { /* no-op */ }
        return false;
    });
    _markStage('afterGhost', filtered);

    // Summarize ghost drops for the current pass so the first ghost-heavy
    // query shows up in the console without flooding subsequent renders.
    try {
        if (typeof window !== 'undefined' && window.__ghostDropStats?.count > 0) {
            const stats = window.__ghostDropStats;
            console.log(`[GhostFilter] 🫥 Dropped ${stats.count} non-renderable cards in this pass. Samples:`, stats.samples);
            window.__ghostDropStats = { count: 0, samples: [] };
        }
    } catch { /* no-op */ }

    // ── Stale game filter: drop bets for games that ended 4+ hours ago ──
    // Handles ISO strings, epoch numbers, Firestore Timestamp objects ({_seconds,_nanoseconds}),
    // and fires on explicit end flags (_isEnded / status='final'|'completed'|'ended').
    // Fails OPEN for unparseable dates — status checks still catch ended games, and
    // letting well-formed unknowns through is safer than wiping the whole results page.
    // Uses _nowMs (= referenceTime in freeze mode) so archived games aren't dropped.
    const _staleMs = _nowMs - 4 * 60 * 60 * 1000;
    const _parseDateMs = (v) => {
        if (v == null || v === '') return null;
        if (typeof v === 'number') return v > 1e12 ? v : v * 1000;
        if (typeof v === 'object') {
            // Firestore Timestamp shape (client or server serialized)
            if (typeof v._seconds === 'number') {
                return v._seconds * 1000 + Math.floor((v._nanoseconds || 0) / 1e6);
            }
            if (typeof v.seconds === 'number') {
                return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
            }
            if (typeof v.toMillis === 'function') {
                try { return v.toMillis(); } catch { /* ignore */ }
            }
            if (v instanceof Date) return v.getTime();
            return null;
        }
        const ms = new Date(v).getTime();
        return Number.isFinite(ms) ? ms : null;
    };
    let _staleDropped = 0;
    let _staleKeptUnparseable = 0;
    let _sampleUnparseable = null;
    const _preStaleList = filtered;
    const _preStaleCount = filtered.length;
    // Freeze/archive mode (referenceTime set): keep final/ended games. The
    // transform layer already kept them in fetchedOptions; dropping them here
    // produced "158 matched, filters hid every result" with every chip cleared.
    const _freezeArchive = !!referenceTime;
    filtered = filtered.filter(option => {
        if (option?._noOddsYet) return true;
        const raw = option?.raw || {};
        const status = String(raw.status || option?.status || '').toLowerCase();
        if (!_freezeArchive && (raw._isEnded === true || option?._isEnded === true ||
            status === 'final' || status === 'completed' || status === 'ended')) {
            _staleDropped++;
            return false;
        }
        const rawDate =
            option?.date ||
            raw.start_date ||
            raw.commence_time ||
            raw.event_date ||
            raw.game_time ||
            raw.start_time ||
            raw._start_date || null;
        const ms = _parseDateMs(rawDate);
        if (ms == null) {
            // Fail OPEN: unknown date shape shouldn't nuke the whole results page.
            // Status checks above still catch explicitly-ended games.
            _staleKeptUnparseable++;
            if (!_sampleUnparseable) {
                _sampleUnparseable = {
                    dateField: rawDate,
                    dateType: typeof rawDate,
                    keys: Object.keys(raw).filter(k => /date|time|start|commence|event/i.test(k)),
                };
            }
            return true;
        }
        if (ms >= _staleMs) return true;
        _staleDropped++;
        return false;
    });
    // Backend already returned these bets. If the stale pass would empty the
    // whole pool (typed search / Background Query View missing referenceTime
    // while production is still on the May freeze), keep the cards. Mixed
    // live+ended pools still drop the old ones.
    if (filtered.length === 0 && _preStaleCount > 0) {
        console.warn('[StaleFilter] would have wiped every result; keeping the pool', {
            preCount: _preStaleCount,
            dropped: _staleDropped,
            freezeArchive: _freezeArchive,
            nowMs: _nowMs,
        });
        filtered = _preStaleList;
    }
    _markStage('afterStale', filtered);
    if (_staleDropped > 0 || _staleKeptUnparseable > 0) {
        console.log('[StaleFilter] ' + JSON.stringify({
            preCount: _preStaleCount,
            postCount: filtered.length,
            dropped: _staleDropped,
            keptUnparseable: _staleKeptUnparseable,
            sampleUnparseable: _sampleUnparseable,
        }));
    }

    // ── Unreliable pricing filter: exclude results where ALL books are prediction
    // markets/exchanges OR offshore-only books. These either use different margin
    // structures (prediction markets) or have very high vig with no US market access
    // (offshore), making EV% calculations misleading for US sports bettors.
    // When only these books have priced a future game, EV will appear strongly
    // negative even for reasonable outcomes — same root cause as the Pistons/Pacers
    // and Hurricanes/Flyers issues. ─────────────────────────────────────────────
    const UNRELIABLE_BOOKS = new Set([
        // Prediction markets / exchanges
        'kalshi', 'polymarket', 'robinhood', 'crypto.com', 'betdex',
        'betfair exchange', 'betfair exchange lay', 'betfair exchange australia',
        'smarkets', 'prophet exchange', 'sporttrade',
        // Common offshore books (not US-accessible, high vig, price early lines)
        '22bet', '1xbet', 'campeonbet', 'bc.game', 'betplay', 'betwhale',
        'parimatch', 'rivalry', 'jugabet', 'winpot', 'playdoit', 'team mexico',
        'ninja casino', 'lucky casino', 'lottoland', 'surgebet', 'betdeluxe',
        'jackpot.bet', 'sportzino', 'midnite', 'betmania', 'betnow', 'betonusa',
        'nitrogen', 'intertops', 'heritage', 'everygame', 'bodog', 'busr',
        'justbet', 'youwager', 'jazz sports', 'mybookie', 'xbet', 'betdsi',
        'sportsbetting.ag', 'betus', 'lowvig', 'betanysports', 'looselines',
    ]);
    const _isUnreliableBook = (name) => {
        if (!name) return false;
        const n = name.toLowerCase();
        if (UNRELIABLE_BOOKS.has(n)) return true;
        return n.includes('polymarket') || n.includes('kalshi') ||
               n.includes('exchange') || n.includes('prediction') ||
               n.includes('crypto.com') || n.includes('parimatch') ||
               n.includes('jugabet') || n.includes('1xbet') ||
               n.includes('betplay') || n.includes('rivalry');
    };
    // Keep backward-compat alias used below
    const _isPredictionBook = _isUnreliableBook;
    const _beforeUnreliable = filtered;
    filtered = filtered.filter(option => {
        if (option?._noOddsYet) return true;
        // SGPs carry only real /parlay/odds prices: every book shows, Sportzino and
        // Midnite included (Trev, Nov 3: only the user's own filters hide a book).
        if ((option?.leg_count || option?.raw?.leg_count || 0) > 1 || option?._fromSGPCache) return true;
        const books = option?.availableBooks ||
                      (option?.bookOdds ? Object.keys(option.bookOdds) : null);
        if (!books || books.length === 0) return true;
        // Keep if at least one traditional sportsbook is present
        return books.some(b => !_isPredictionBook(b));
    });
    // Relaxed view ("show what we have"): when only prediction-market / offshore
    // prices exist (Royals NRFI: Kalshi + Polymarket only, Oct 1, 2026), show them
    // with a note instead of "0 of 2". The note reads `unreliableOnlyBooks`.
    let _unreliableOnlyBooks = null;
    if (disableMinBooksFilter && filtered.length === 0 && _beforeUnreliable.length > 0) {
        filtered = _beforeUnreliable;
        const names = new Set();
        for (const o of filtered) for (const b of (o?.availableBooks || Object.keys(o?.bookOdds || {}))) names.add(b);
        _unreliableOnlyBooks = [...names].slice(0, 4);
    }
    _markStage('afterUnreliableBooks', filtered);
    
    // STRICT DATE FILTERING: Extract date from query and filter by it
    if (!disableDateKeywordFilter && rq && rq.trim()) {
        const queryLower = rq.toLowerCase();
        let targetDate = null;
        
        // Parse temporal keywords from query.
        // Compare LOCAL dates, not UTC: an evening game "tonight" (e.g. 7 PM MT)
        // has a UTC date of tomorrow, so UTC comparison silently dropped every
        // evening game from "tonight" queries. en-CA locale gives YYYY-MM-DD.
        // When referenceTime is set (archive/freeze/lab), "tonight" means that
        // frozen day's local date — not wall-clock today. Using Date.now() here
        // wiped every captured "best bets tonight" payload whose games were on
        // a different calendar day than the operator's machine.
        const _localYMD = (dt) => dt.toLocaleDateString('en-CA');
        const now = Number.isFinite(_nowMs) ? new Date(_nowMs) : new Date();
        if (/\btonight\b|\btoday\b/.test(queryLower)) {
            targetDate = _localYMD(now); // YYYY-MM-DD (local)
        } else if (/\btomorrow\b/.test(queryLower)) {
            const tomorrow = new Date(now);
            tomorrow.setDate(tomorrow.getDate() + 1);
            targetDate = _localYMD(tomorrow);
        }
        
        // Apply date filter strictly if we detected a temporal keyword
        if (targetDate) {
            filtered = filtered.filter(option => {
                // PRIORITY 1: Extract date from the item ID (format: "33152-35142-2025-10-22:draftkings:...")
                let gameDateStr = '';
                const itemId = option?.raw?.id || '';
                const idDateMatch = itemId.match(/(\d{4})-(\d{2})-(\d{2})/);
                
                if (idDateMatch) {
                    gameDateStr = `${idDateMatch[1]}-${idDateMatch[2]}-${idDateMatch[3]}`;
                } else {
                    // PRIORITY 2: Check other date fields
                    const gameDate = option?.date || 
                                   option?.raw?.commence_time || 
                                   option?.raw?.event_date ||
                                   option?.raw?.game_time ||
                                   option?.raw?.start_date || 
                                   option?.raw?.start_time || 
                                   option?.raw?.date || 
                                   option?.raw?.timestamp || '';
                    
                    if (!gameDate) {
                        console.warn('[SGP] No date found for option:', option?.title, 'ID:', itemId);
                        return false; // Filter out if no date (strict filtering for "tomorrow")
                    }
                    
                    // Convert to YYYY-MM-DD format for comparison.
                    // Handle serialized Firestore Timestamps ({_seconds}/{seconds}) and
                    // epoch numbers first — new Date() can't parse those object shapes,
                    // which previously made gameDateStr '' and dropped every cached SGP
                    // on "tonight"/"today"/"tomorrow" queries.
                    let _gd = gameDate;
                    if (typeof _gd === 'object') {
                        if (typeof _gd._seconds === 'number') _gd = _gd._seconds * 1000;
                        else if (typeof _gd.seconds === 'number') _gd = _gd.seconds * 1000;
                        else if (typeof _gd.toMillis === 'function') { try { _gd = _gd.toMillis(); } catch { /* keep */ } }
                    }
                    if (typeof _gd === 'number' && _gd < 1e12) _gd = _gd * 1000;
                    try {
                        const d = new Date(_gd);
                        if (isNaN(d.getTime())) {
                            // If date is invalid, try to extract YYYY-MM-DD from string
                            const match = String(gameDate).match(/(\d{4})-?(\d{2})-?(\d{2})/);
                            if (match) {
                                gameDateStr = `${match[1]}-${match[2]}-${match[3]}`;
                            }
                        } else {
                            // Local date — must match the local targetDate above
                            gameDateStr = d.toLocaleDateString('en-CA');
                        }
                    } catch {
                        // If date parsing fails, try to extract YYYY-MM-DD from string
                        const match = String(gameDate).match(/(\d{4})-?(\d{2})-?(\d{2})/);
                        if (match) {
                            gameDateStr = `${match[1]}-${match[2]}-${match[3]}`;
                        }
                    }
                }
                
                const matches = gameDateStr === targetDate;
                if (!matches) {
                    console.log('[SGP] Date Filter EXCLUDED:', { 
                        targetDate, 
                        gameDateStr, 
                        itemId: itemId.substring(0, 60), 
                        title: option?.title?.substring(0, 40) 
                    });
                }
                return matches;
            });
            
            console.log(`[SGP] Date filtered for ${targetDate}: ${filtered.length} of ${fetchedOptions.length} bets matched`);
        }
    }
    _markStage('afterDateKeyword', filtered);
    
    // Apply query-based relevance filter (remove completely irrelevant results)
    // BUT skip this filtering if the results were broadened (user explicitly asked for broader results)
    if (!disableRelevanceFilter && rq && rq.trim() && !didBroaden) {
        const queryLower = rq.toLowerCase();
        // normalizeQuery expands typos/abbreviations AND annotates tonight/today
        // with `(YYYY-MM-DD)`. Those dates are for the date-keyword filter above;
        // treating them as required relevance tokens wipes every result whose
        // team/market text doesn't contain today's ISO date (lab + live).
        const normalizedQueryLower = String(normalizeQuery(rq) || rq)
            .toLowerCase()
            .replace(/\(\d{4}-\d{2}-\d{2}\)/g, ' ');
        const _skipTerms = new Set(['the', 'and', 'bet', 'for', 'best', 'game', 'state', 'golden', 'moneyline', 'spread', 'total', 'over', 'under', 'parlays', 'parlay']);
        const queryTerms = [...new Set([
            ...queryLower.split(/\s+/),
            ...normalizedQueryLower.split(/\s+/)
        ])].map(t => t.replace(/[()]/g, ''))
          .filter(t => t.length >= 3 && !_skipTerms.has(t) && !/^\d{4}-\d{2}-\d{2}$/.test(t));
        // What the backend resolved ("bruson" → jalen brunson) also counts as relevant.
        const resolvedTerms = resolvedRelevanceTerms(responseMetadata);
        
        // Only filter if we have meaningful terms (like team names, player names, or league abbreviations)
        // Don't filter if we only have generic terms
        // ⭐ Expanded list includes leagues (nba, nfl, etc.) since those already filter at cache level
        const genericTerms = [
            'today', 'tonight', 'tomorrow', 'week', 'weekend',
            'games', 'bets', 'props', 'picks', 'best', 'top', 'good',
            'all', 'any', // classification sentinels ("all books", league: "all")
            // League names (already filtered at cache level, shouldn't re-filter at display level)
            'nba', 'nfl', 'nhl', 'mlb', 'ncaaf', 'ncaab', 'wnba', 'mls',
            'atp', 'wta', 'tennis',
            'basketball', 'football', 'hockey', 'baseball', 'soccer'
        ];
        const hasSpecificTerms = queryTerms.some(term => !genericTerms.includes(term));
        
        if (queryTerms.length > 0 && hasSpecificTerms) {
            filtered = filtered.filter(option => {
                // Futures were matched by the backend on award synonyms ("dpoy" →
                // Defensive Player Of The Year, "championship" → Super Bowl Winner).
                if (option?.raw?._isFuture) return true;
                const fields = [
                    (option?.raw?.home_team || '').toLowerCase(),
                    (option?.raw?.away_team || '').toLowerCase(),
                    (option?.raw?.team || '').toLowerCase(),
                    (option?.raw?.player || '').toLowerCase(),
                    (option?.raw?.player_name || '').toLowerCase(),
                    (option?.raw?.selection || '').toLowerCase(),
                    (option?.raw?.market || '').toLowerCase(),
                    (option?.raw?.league || '').toLowerCase(),
                    (option?.raw?.sport || '').toLowerCase(),
                    (option?.description || '').toLowerCase(),
                ].join(' ');
                
                // Check if ANY query term (typed or resolved) appears in the fields
                return queryTerms.some(term => fields.includes(term))
                    || resolvedTerms.some(term => fields.includes(term));
            });
        }
        // If only generic terms (like "best parlays tomorrow"), don't filter - show all API results
    }
    _markStage('afterRelevance', filtered);
    
    // Apply search text filter (includes team, player, market, AND sportsbook names)
    if (searchText.trim()) {
        const search = searchText.toLowerCase();
        filtered = filtered.filter(option => {
            const title = (option?.title || '').toLowerCase();
            const desc = (option?.description || '').toLowerCase();
            const selection = (option?.raw?.selection || '').toLowerCase();
            const market = (option?.raw?.market || '').toLowerCase();
            const legMarketsStr = getSgpLegMarketLabels(option).join(' ').toLowerCase();
            const playerName = (option?.raw?.player_name || '').toLowerCase();
            const teamName = (option?.raw?.team || option?.raw?.home_team || '').toLowerCase();
            const awayTeam = (option?.raw?.away_team || '').toLowerCase();
            // Include sportsbook names in search
            const bookOdds = option?.bookOdds || option?.raw?.bookOdds || {};
            const bookNames = Object.keys(bookOdds).join(' ').toLowerCase();
            return title.includes(search) || desc.includes(search) || 
                   selection.includes(search) || market.includes(search) ||
                   legMarketsStr.includes(search) ||
                   playerName.includes(search) || teamName.includes(search) ||
                   awayTeam.includes(search) || bookNames.includes(search);
        });
    }
    _markStage('afterSearchText', filtered);
    
    // Apply sportsbook filter across allBooks / bookOdds / availableBooks,
    // with normalized names so "DraftKings" matches "draftkings".
    if (filterSportsbooks.length > 0) {
        filtered = filtered.filter(option => optionMatchesSportsbookFilters(option, filterSportsbooks));
    }
    
    // Apply market filter (SGPs: match leg markets; see optionMatchesMarketFilters)
    if (filterMarkets.length > 0) {
        filtered = filtered.filter(option => optionMatchesMarketFilters(option, filterMarkets));
    }
    // Player chips: names are split (combo/H2H) and accent-free, see optionMatchesPlayerFilters
    if (filterPlayers.length > 0) {
        filtered = filtered.filter(option => optionMatchesPlayerFilters(option, filterPlayers));
    }

    // Apply leg count filter (SGP mode only)
    if (filterLegCount !== null) {
        filtered = filtered.filter(option => {
            const lc = option?.leg_count || option?._sgpData?.leg_count || option?.legs?.length;
            return Number(lc) === filterLegCount;
        });
    }
    
    // Apply result type filter (team odds vs player props)
    // Uses the same 6-condition _isOptionPlayerProp helper as hasPropsResults / availablePlayers
    if (filterResultType === 'team') {
        filtered = filtered.filter(option => !_isOptionPlayerProp(option));
    } else if (filterResultType === 'props') {
        filtered = filtered.filter(option => _isOptionPlayerProp(option));
    }
    
    // Apply deep links filter - only show bets that have at least one deep link
    if (filterDeepLinksOnly) {
        const beforeCount = filtered.length;
        filtered = filtered.filter(option => {
            const bookOdds = option?.bookOdds || option?.raw?.bookOdds || {};
            // Check if any book has a valid deep_link
            const hasDeepLink = Object.entries(bookOdds).some(([bookName, bookData]) => {
                if (!bookData || typeof bookData !== 'object') return false;
                const dl = bookData.deep_link || bookData.deeplink;
                if (!dl) return false;
                // Handle both string and object formats
                if (typeof dl === 'string') return dl.length > 0;
                if (typeof dl === 'object') return !!(dl.desktop || dl.ios || dl.android);
                return false;
            });
            return hasDeepLink;
        });
        console.log(`[DeepLink Filter] Filtered ${beforeCount} → ${filtered.length} results with deep links`);
        
        // Debug: Show sample of what bookOdds looks like
        if (beforeCount > 0 && filtered.length === 0) {
            const sample = fetchedOptions[0];
            const sampleBookOdds = sample?.bookOdds || sample?.raw?.bookOdds || {};
            console.log('[DeepLink Filter] ⚠️ No results have deep links! Sample bookOdds:', 
                Object.entries(sampleBookOdds).slice(0, 2).map(([book, data]) => ({
                    book,
                    hasDeepLink: !!(data?.deep_link || data?.deeplink),
                    dataKeys: Object.keys(data || {})
                }))
            );
        }
    }

    // ⭐ Positive-EV only filter — drops items whose header-pill EV would be ≤ 0.
    // Uses the SAME axis as the visible card pill so the filter and display always agree:
    //   1. Focus-book → _sortEvForBook at that book's price (SGPs: min-of-three, straights: cleanPerBookEv)
    //   2. My Books mode → best My Books EV (ev_percent_mybooks, stamped by annotateCleanEv)
    //   3. Default → walk bookOdds via _sortEvForBook, take the best book (same as grid resort)
    //
    // Previously used ev_percent_clean → ev_percent (headline) for the default path, which uses
    // min(mult,pow) leg fair probs. The card pill uses min(add,mult,pow) — additive can be much
    // lower on correlated longshots, causing cards with negative pills to pass the filter (or vice versa).
    // Sportsbook / market / player / result-type chips the user picked.
    _markStage('afterChips', filtered);
    if (filterPositiveEvOnly) {
        const beforeEvCount = filtered.length;
        const _myBooksActive = _wantsMyBooksMode || sortBy === 'mybooks';
        const _focusRaw = preferredSportsbook ||
            (filterSportsbooks.length === 1 ? filterSportsbooks[0] : null);
        const _normB = (s) => String(s || '').toLowerCase()
            .replace(/\s*\([^)]*\)\s*$/, '')
            .replace(/[^a-z0-9]+/g, '');
        const _focusFp = _focusRaw ? _normB(_focusRaw) : null;

        filtered = filtered.filter(option => {
            const r = option?.raw || option || {};
            const bo = r.bookOdds || option?.bookOdds || {};
            let v = null;

            if (_focusFp) {
                // Focus-book mode: use _sortEvForBook so SGPs use min-of-three, not stored headline
                for (const [bk, bd] of Object.entries(bo)) {
                    if (_normB(bk) === _focusFp) {
                        v = _sortEvForBook(r, bd);
                        // Fallback for items without method stamps (old cache, theoretical)
                        if (!Number.isFinite(v)) v = bd?.clean_ev_percent ?? bd?.ev_percent ?? bd?.ev ?? null;
                        break;
                    }
                }
                // If focused book not present on this card, exclude it
                if (!Number.isFinite(v)) return false;
            } else if (_myBooksActive && Number.isFinite(r.ev_percent_mybooks)) {
                v = r.ev_percent_mybooks;
            } else {
                // Default: walk all books with the card-consistent formula
                for (const [, bd] of Object.entries(bo)) {
                    const wc = _sortEvForBook(r, bd);
                    if (Number.isFinite(wc) && (v === null || wc > v)) v = wc;
                }
                // Fallback for legacy/theoretical items with no bookOdds method stamps
                if (!Number.isFinite(v) && Number.isFinite(r.ev_percent_clean)) v = r.ev_percent_clean;
                if (!Number.isFinite(v) && Number.isFinite(r.ev_percent)) v = r.ev_percent;
            }

            return Number.isFinite(v) && v > 0;
        });
        console.log(`[+EV Filter] ${beforeEvCount} → ${filtered.length} (focusBook=${_focusRaw || 'none'}, myBooks=${_myBooksActive})`);
    }
    _markStage('afterPositiveEv', filtered);
    
    // ⭐ Min-books quality threshold: exactly the user's saved preference.
    // Props always enforce a floor of 2; team bets enforce the user's minBooksStraights pref.
    // SGP options use minBooksSGP. Book count is measured against TRADITIONAL books only
    // (prediction market / exchange books are excluded from the threshold).
    //
    // ⭐ MY BOOKS MODE: When sortBy='mybooks' OR defaultBookSort='mybooks', the threshold
    // counts only books in the user's My Books list. Saying "My Books + minBooks=5"
    // means: only show bets that at least 5 of YOUR books carry.
    // Counted on the card's own deduped list (utils/cardBooks: lay feeds
    // dropped, regional variants folded), minus prediction books, so a card
    // that passes "5+" never shows fewer than 5 books (Oct 2026).
    const _getTradBookCount = (o) => {
        const bo = lineBookOdds(o);
        const books = Object.keys(bo).length > 0
            ? dedupeCardBooks(bo).map(e => e.book)
            : [...new Set((o?.availableBooks || [])
                .filter(b => b && !isLayBookName(b))
                .map(canonicalBookName))];
        if (books.length > 0) {
            const tradCount = books.filter(b => !_isPredictionBook(b)).length;
            if (tradCount > 0) return tradCount;
            return books.length;
        }
        return o?.raw?._sportsbook_count || 1;
    };
    if (!disableMinBooksFilter) {
        // Always count TOTAL traditional books for the min-books quality threshold,
        // regardless of mode. My Books mode distinction is handled by:
        //   1. Source selection (_useMyBooksSet above)
        //   2. EV sort axis (ev_percent_mybooks when _wantsMyBooksMode)
        //   3. Hard filter below (≥1 My Book must be present)
        // Counting only the user's books for the threshold was too restrictive —
        // "needs 5 of YOUR 9 books" eliminated nearly everything.
        const _myBooksLower = _myBooksList.map(b => b.toLowerCase());
        const _bookIsMyBook = (b) => {
            if (!b) return false;
            const bl = String(b).toLowerCase();
            return _myBooksLower.some(mb => bl === mb || bl.includes(mb) || mb.includes(bl));
        };
        const _getBC = _getTradBookCount;

        // ⭐ HONEST FILTERS (v1.51): the threshold is exactly the user's saved
        // minimum — never silently ramped above it. The old "dynamic threshold"
        // raised the bar to whatever the 200th-most-covered option had (often 8+),
        // which deleted high-EV bets with 5-7 books before the user ever saw them.
        // Display volume is pagination's job, not a hidden filter's.
        const userMinBooks = effectiveMinBooksStraights(userProfile?.preferences?.minBooksStraights, _hideNoEv);
        const userMinBooksSGP = effectiveMinBooksSGP(userProfile?.preferences?.minBooksSGP, _hideNoEv);
        const dynamicThreshold = userMinBooks;

        // Per CARD, not per line (Oct 9, 2026): a card (cardGroupKey: game +
        // subject + market, both sides) passes when its best line has enough
        // books, and then keeps every line. Per line, alt lines with 1–4 books
        // were removed before grouping, so the card showed one line and hid
        // its alt-lines toggle. Options that render alone are judged alone.
        const _cardBestBooks = new Map();
        for (const option of filtered) {
            if ((option?.leg_count || option?.raw?.leg_count || 0) > 1 || option?._fromSGPCache) continue;
            const key = cardGroupKey(option);
            if (key === null) continue;
            const bc = _getBC(option);
            if (!(_cardBestBooks.get(key) >= bc)) _cardBestBooks.set(key, bc);
        }
        const beforeMinBooks = filtered.length;
        filtered = filtered.filter(option => {
            if (option?._noOddsYet) return true;
            // Futures: awards and longshot markets are often priced by a handful of
            // books; the card's own Min-books filter still applies inside it.
            if (option?.raw?._isFuture) return true;
            const isSGPOption = (option?.leg_count || option?.raw?.leg_count || 0) > 1 || !!option?._fromSGPCache;
            if (isSGPOption) return _getBC(option) >= userMinBooksSGP;
            const key = cardGroupKey(option);
            const bookCount = key === null ? _getBC(option) : _cardBestBooks.get(key);
            const isProp = _isOptionPlayerProp(option);
            // The 2-book prop floor is an automatic default too: none when "All".
            const minThreshold = isProp && dynamicThreshold > 0 ? Math.max(dynamicThreshold, 2) : dynamicThreshold;
            return bookCount >= minThreshold;
        });
        if (beforeMinBooks !== filtered.length) {
            console.log(`[MinBooks Filter] ${beforeMinBooks} → ${filtered.length} (need ${dynamicThreshold}+ traditional books)`);
        }
    }
    _markStage('afterMinBooks', filtered);
    
    // ⭐ MY BOOKS HARD FILTER: when My Books is the active axis (either
    // sortBy==='mybooks' OR the preference is on with >=1 book chosen), drop
    // every item where no My Book carries the bet so sort order, header
    // pill, and sidebar all operate on the same axis with no silent
    // global-EV leak-through.
    //
    // SAFETY NET: if the filter would zero out the list (no backend result
    // has any overlap with the user's My Books — common for niche SGPs and
    // player-prop queries hitting DFS-heavy markets), we KEEP the full list
    // instead of showing "0 of 100 Bets". Sort still falls through to
    // My Books EV where present and nulls-last behavior handles the rest,
    // so the user sees actionable data with a clear visual signal that
    // none of their books carry it — not a blank page.
    {
        const _mbHardFilterActive = (_wantsMyBooksMode || sortBy === 'mybooks') && _myBooksList.length > 0;
        if (_mbHardFilterActive) {
            const _mbNorm = new Set(_myBooksList.map(b => normBookName(b)));
            // ⭐ v1.43.25: Enforce minBooksMyBooks threshold (default 3), not just ≥1.
            // SGPs typically only have DraftKings + FanDuel, so cap their requirement at 1.
            // Show-everything (hideNoEvRows off): any one of your books, unless
            // you saved a different minimum (utils/searchDisplay).
            const _mbMin = Math.min(
                effectiveMinMyBooks(userProfile?.preferences?.minBooksMyBooks, _hideNoEv),
                _myBooksList.length
            );
            const _countMyBooks = (option) => {
                const r = option?.raw || option || {};
                const bo = r.bookOdds || option?.bookOdds || {};
                return Object.keys(bo).filter(bk => _mbNorm.has(normBookName(bk))).length;
            };
            const _isSGPOption = (option) =>
                (option?.leg_count || option?.raw?.leg_count || 0) > 1 || !!option?._fromSGPCache;
            const _passesMyBooks = (option) => {
                const r = option?.raw || option || {};
                const isSGP = _isSGPOption(option);
                // SGPs: just need 1 My Book (most books don't offer SGPs)
                const threshold = isSGP ? 1 : _mbMin;
                // Fast path: server already computed ev_percent_mybooks → at least 1 My Book confirmed
                if (isSGP && Number.isFinite(r.ev_percent_mybooks)) return true;
                return _countMyBooks(option) >= threshold;
            };
            const beforeMb = filtered.length;
            const kept = filtered.filter(_passesMyBooks);
            if (kept.length === 0 && beforeMb > 0) {
                console.log(`[MyBooks HardFilter] Skipped drop — 0/${beforeMb} passed My Books threshold (${_mbMin}+); keeping full list.`);
            } else {
                if (beforeMb !== kept.length) {
                    console.log(`[MyBooks HardFilter] Dropped ${beforeMb - kept.length} items below My Books threshold (${_mbMin}+) (${beforeMb} → ${kept.length})`);
                }
                filtered = kept;
            }
        }
    }
    _markStage('afterMyBooks', filtered);

    // MAIN/ALT line type. Freeze often omits is_main — infer from the pool
    // (most-covered number = main) for props AND game lines so Alt actually
    // changes the list. Counts are taken before this filter so chips stay honest.
    const preLineType = filtered;
    const lineTypeCounts = countLineTypeSplits(preLineType);
    if (filterLineType && filterLineType !== 'all') {
        const beforeLineType = filtered.length;
        filtered = filtered.filter(option => optionPassesLineTypeFilter(option, filterLineType, preLineType));
        console.log(`[LineType Filter] ${filterLineType}: ${beforeLineType} → ${filtered.length} results`);
    }
    _markStage('afterAllFilters', filtered);
    try {
        if (typeof window !== 'undefined') {
            window.__filterStageStats = _stageCounts;
        }
    } catch { /* no-op */ }
    
    // Apply sorting
    const sorted = [...filtered];
    
    // ⭐ Helper: Get filter relevance score for sorting
    // When multiple market filters are selected, sort by their selection order (which is relevance-based)
    const getFilterRelevanceScore = (option) => {
        if (filterMarkets.length <= 1) return 0; // No need if only 0-1 filters
        
        const legMs = getSgpLegMarketLabels(option).map(m => m.toLowerCase());
        let bestIdx = 999;
        if (legMs.length > 0) {
            filterMarkets.forEach((fm, i) => {
                const fl = fm.toLowerCase();
                if (legMs.some(lm => lm === fl || lm.includes(fl) || fl.includes(lm))) {
                    bestIdx = Math.min(bestIdx, i);
                }
            });
            if (bestIdx < 999) return bestIdx;
        }
        const optionMarket = (option?.raw?.market || option?.raw?.market_id || option?.title || '').toLowerCase();
        const filterIndex = filterMarkets.findIndex(fm => fm.toLowerCase() === optionMarket);
        return filterIndex === -1 ? 999 : filterIndex;
    };
    
    // ⭐ Helper: Get query relevance score (for both filters and general sorting)
    // STRICT matching: For multi-part queries, ALL parts must match for high score
    // ⭐ NEW: Prioritize single-player markets over combo markets
    const getQueryRelevanceScore = (option) => {
        const market = option?.raw?.market || option?.raw?.market_id || option?.title || '';
        const legMs = getSgpLegMarketLabels(option);
        let score = legMs.length > 0
            ? Math.max(0, ...legMs.map(m => getMarketRelevanceToQuery(m, rq)))
            : getMarketRelevanceToQuery(market, rq);
        
        if (!rq) return score;
        
        const queryLower = rq.toLowerCase();
        
        // ⭐ Detect multi-part queries (using + or "and")
        const queryParts = queryLower.split(/\s*[\+&]\s*|\s+and\s+/).map(p => p.trim()).filter(p => p);
        
        // Extract meaningful search terms from EACH part separately
        const extractTermsFromPart = (part) => {
            return part
                .replace(/\b(sgp|sgps|parlay|parlays|same game|today|tonight|tomorrow|best|top|ml|moneyline|spread|over|under|1st|2nd|3rd|4th|quarter|half|vs|versus)\b/gi, '')
                .split(/\s+/)
                .filter(t => t.length >= 3);
        };
        
        // For SGPs, get all leg text
        const legs = option?.raw?.legs || [];
        const legTexts = legs.map(leg => 
            `${leg.name || ''} ${leg.selection || ''} ${leg.player || ''} ${leg.team || ''}`.toLowerCase()
        );
        
        // Also check main option fields
        const mainText = [
            option?.raw?.home_team || '',
            option?.raw?.away_team || '',
            option?.raw?.team || '',
            option?.raw?.player || '',
            option?.raw?.selection || '',
            option?.title || '',
            option?.description || ''
        ].join(' ').toLowerCase();
        
        const allText = mainText + ' ' + legTexts.join(' ');
        
        // ⭐ TEAM-SPECIFIC LEG MATCHING
        // When searching "suns sgps", we want SGPs with "Suns ML" or Suns player props FIRST
        // Not just any SGP from a Suns vs Warriors game
        const homeTeam = (option?.raw?.home_team || '').toLowerCase();
        const awayTeam = (option?.raw?.away_team || '').toLowerCase();
        
        // Extract team search terms (excluding common words)
        const teamSearchTerms = extractTermsFromPart(queryLower).filter(term => 
            // Keep terms that might be team names (not player stat keywords)
            !['points', 'assists', 'rebounds', 'blocks', 'steals', 'threes', 'goals', 'saves', 'rushing', 'passing', 'receiving', 'touchdowns', 'shots'].includes(term)
        );
        
        // ⭐ Helper: Escape special regex characters to prevent crashes (e.g., "+40")
        const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        // Check if any search term is likely a team name (appears in home or away team)
        const searchedTeamTerms = teamSearchTerms.filter(term => {
            try {
                const termRegex = new RegExp(`\\b${escapeRegex(term)}\\b`, 'i');
                return termRegex.test(homeTeam) || termRegex.test(awayTeam);
            } catch (e) {
                // If regex still fails, fall back to includes
                return homeTeam.includes(term) || awayTeam.includes(term);
            }
        });
        
        // ⭐ COUNT HOW MANY LEGS DIRECTLY REFERENCE THE SEARCHED TEAM
        let legsWithSearchedTeam = 0;
        let legsWithOpponentOnly = 0;
        
        if (searchedTeamTerms.length > 0 && legs.length > 0) {
            // Determine which team the user is searching for
            const isSearchingHome = searchedTeamTerms.some(term => {
                try {
                    const termRegex = new RegExp(`\\b${escapeRegex(term)}\\b`, 'i');
                    return termRegex.test(homeTeam);
                } catch { return homeTeam.includes(term); }
            });
            const isSearchingAway = searchedTeamTerms.some(term => {
                try {
                    const termRegex = new RegExp(`\\b${escapeRegex(term)}\\b`, 'i');
                    return termRegex.test(awayTeam);
                } catch { return awayTeam.includes(term); }
            });
            
            const searchedTeamName = isSearchingHome ? homeTeam : (isSearchingAway ? awayTeam : '');
            const opponentTeamName = isSearchingHome ? awayTeam : (isSearchingAway ? homeTeam : '');
            
            legs.forEach(leg => {
                const legText = `${leg.name || ''} ${leg.selection || ''} ${leg.player || ''} ${leg.team || ''}`.toLowerCase();
                
                // Check if this leg is about the searched team
                const legMentionsSearchedTeam = searchedTeamTerms.some(term => {
                    try {
                        const termRegex = new RegExp(`\\b${escapeRegex(term)}\\b`, 'i');
                        return termRegex.test(legText);
                    } catch { return legText.includes(term); }
                });
                
                // Check if leg mentions opponent but not searched team
                const opponentLastWord = opponentTeamName ? opponentTeamName.split(' ').pop() : '';
                let legMentionsOpponent = false;
                if (opponentLastWord) {
                    try {
                        legMentionsOpponent = new RegExp(`\\b${escapeRegex(opponentLastWord)}\\b`, 'i').test(legText);
                    } catch { legMentionsOpponent = legText.includes(opponentLastWord); }
                }
                
                if (legMentionsSearchedTeam) {
                    legsWithSearchedTeam++;
                } else if (legMentionsOpponent) {
                    legsWithOpponentOnly++;
                }
            });
            
            // ⭐ SCORING BONUS/PENALTY based on leg team specificity
            if (legsWithSearchedTeam > 0) {
                // SGP has at least one leg about the searched team - BONUS!
                score += 200 * legsWithSearchedTeam;
                
                // Extra bonus if ALL legs are about the searched team
                if (legsWithSearchedTeam === legs.length) {
                    score += 300;
                }
            } else if (legsWithOpponentOnly > 0) {
                // SGP has NO legs about searched team, only opponent - PENALTY
                score -= 150 * legsWithOpponentOnly;
            }
        }
        
        // ⭐ STRAIGHT BET TEAM MATCHING (non-SGP)
        // For queries like "kings 2nd half spread", boost items where selection contains searched team
        if (searchedTeamTerms.length > 0 && legs.length === 0) {
            const selectionText = (option?.description || option?.raw?.selection || option?.raw?.name || '').toLowerCase();
            const rawTeam = (option?.raw?.team || '').toLowerCase();
            
            const selectionMatchesSearchedTeam = searchedTeamTerms.some(term => {
                try {
                    return new RegExp(`\\b${escapeRegex(term)}\\b`, 'i').test(selectionText) || 
                           new RegExp(`\\b${escapeRegex(term)}\\b`, 'i').test(rawTeam);
                } catch { return selectionText.includes(term) || rawTeam.includes(term); }
            });
            
            if (selectionMatchesSearchedTeam) {
                score += 500;
            } else {
                score -= 200;
            }
        }
        
        // ⭐ DETECT COMBO/MULTI-PLAYER MARKETS - penalize these for single-player queries
        const marketLower = market.toLowerCase();
        const titleOriginal = option?.title || '';
        const titleLower = titleOriginal.toLowerCase();
        const descOriginal = option?.description || '';
        const descLower = descOriginal.toLowerCase();
        const combinedMarketText = marketLower + ' ' + titleLower + ' ' + descLower;
        const combinedOriginal = market + ' ' + titleOriginal + ' ' + descOriginal;
        
        // Check if this is a combo market (Either, Each, Combined, +, etc.)
        // Also check for specific patterns like "(Either)" or "(Each)"
        const isComboMarket = /\b(combo|either|each|combined|alternate|both)\b|\+/.test(combinedMarketText)
            || /\((either|each|combo)\)/i.test(combinedOriginal);
        
        // Check if multiple player names appear in the title/description
        // Use original case to detect "Nikola Jokic + Paolo Banchero" patterns
        // Match "Firstname Lastname" patterns (capital letter followed by lowercase)
        const playerNamePattern = /[A-Z][a-z]+\.?\s+[A-Z][a-z]+/g;
        const playerMatches = combinedOriginal.match(playerNamePattern) || [];
        // Also check for " + " which indicates combining players
        const hasPlayerSeparator = combinedOriginal.includes(' + ') || combinedOriginal.includes(' & ');
        let hasMultiplePlayers = playerMatches.length >= 2 || hasPlayerSeparator;
        
        // ⭐ STRICT MATCHING: For each query part, check if terms match
        let matchedParts = 0;
        let totalTermsMatched = 0;
        let playerNameMatches = 0;
        let exactPlayerMatch = false;
        
        queryParts.forEach(part => {
            const terms = extractTermsFromPart(part);
            const partMatches = terms.filter(term => allText.includes(term));
            
            if (partMatches.length > 0) {
                matchedParts++;
                totalTermsMatched += partMatches.length;
                
                // ⭐ Check if this match is in a leg (not just home/away team)
                terms.forEach(term => {
                    if (legTexts.some(legText => legText.includes(term))) {
                        playerNameMatches++;
                        // Check for exact player name match (not part of combo)
                        if (!isComboMarket && !hasMultiplePlayers) {
                            exactPlayerMatch = true;
                        }
                    }
                });
            }
        });
        
        // ⭐ SCORING LOGIC:
        // - If ALL query parts match: high score
        // - If only some parts match: medium score  
        // - If no parts match: penalty
        // - ⭐ NEW: Single player markets get bonus, combo markets get penalty
        
        if (queryParts.length > 0) {
            const matchRatio = matchedParts / queryParts.length;
            
            if (matchRatio === 1) {
                // ALL parts matched - high relevance
                score += 200 + (playerNameMatches * 50);
            } else if (matchRatio >= 0.5) {
                // At least half matched
                score += 50 + (playerNameMatches * 25);
            } else if (matchRatio > 0) {
                // Some matched
                score += 10;
            } else {
                // NOTHING matched - penalty (push to bottom)
                score -= 100;
            }
        }
        
        // ⭐ SINGLE vs COMBO MARKET ADJUSTMENT
        // For single-player queries like "jokic points", prioritize non-combo markets
        const queryTermsFlat = queryParts.flatMap(part => extractTermsFromPart(part));
        const isSinglePlayerQuery = queryParts.length === 1 && queryTermsFlat.length <= 2;
        
        // ⭐ ENHANCED: For SGPs, check if ALL legs are for the same player (single-player SGP)
        // Note: `legs` is already declared above, reuse it
        let isAllSamePlayer = false;
        let hasSinglePlayerLeg = false;
        
        if (legs.length > 0 && queryTermsFlat.length > 0) {
            const playerTerm = queryTermsFlat.find(t => 
                !['points', 'assists', 'rebounds', 'blocks', 'steals', 'goals', 'saves', 'threes'].includes(t)
            );
            
            if (playerTerm) {
                // Check if all legs mention the same player
                const legsWithPlayer = legs.filter(leg => {
                    const legText = ((leg.name || '') + ' ' + (leg.player || '')).toLowerCase();
                    return legText.includes(playerTerm);
                });
                
                isAllSamePlayer = legsWithPlayer.length === legs.length;
                hasSinglePlayerLeg = legsWithPlayer.length >= 1;
                
                // If looking at legs with the player, check if there are OTHER players too
                if (hasSinglePlayerLeg && !isAllSamePlayer && legs.length > 1) {
                    // This is a combo SGP with multiple different players
                    hasMultiplePlayers = true;
                }
            }
        }
        
        if (isSinglePlayerQuery) {
            if (isAllSamePlayer && !isComboMarket) {
                // SGP with ALL legs for the same player - highest priority!
                score += 200;
            } else if (exactPlayerMatch && !isComboMarket && !hasMultiplePlayers) {
                // Pure single-player market - big bonus
                score += 150;
            } else if (hasSinglePlayerLeg && !isAllSamePlayer) {
                // Has the player but also has other players - medium penalty
                score -= 50;
            } else if (isComboMarket || hasMultiplePlayers) {
                // Combo market - significant penalty for single-player queries
                score -= 100;
            }
        }
        
        // ⭐ MARKET SIMPLICITY BONUS
        // For player prop queries, prioritize simple market names over complex ones
        // "Player Points" should appear before "Player Points H2H Spread" or "Player Points (Each)"
        const statKeywords = ['points', 'assists', 'rebounds', 'blocks', 'steals', 'threes', 'goals', 'saves'];
        const queryHasStatKeyword = statKeywords.some(stat => queryLower.includes(stat));
        
        if (queryHasStatKeyword) {
            // Check what kind of market this is
            const isSimpleMarket = statKeywords.some(stat => {
                const simplePattern = new RegExp(`^player\\s+${stat}$`, 'i');
                return simplePattern.test(marketLower.trim());
            });
            
            // Check for complex market patterns that should be deprioritized
            const hasComplexSuffix = /\b(h2h|spread|each|either|combo|alternate|both|\+)\b/i.test(marketLower);
            const hasParentheses = /\([^)]+\)/.test(market);
            
            if (isSimpleMarket && !hasComplexSuffix && !hasParentheses) {
                // Simple "Player Points", "Player Assists", etc. - big bonus
                score += 300;
            } else if (hasComplexSuffix || hasParentheses) {
                // Complex market like "Player Points (Each)" or "Player Points H2H Spread" - penalty
                score -= 150;
            }
        }
        
        return score;
    };
    
    // ⭐ Shared helper: Extract the best available American odds price from an item.
    // Mirrors the bestRawPrice logic in UnifiedBetCard:
    //   - For SGPs: combined_price is the parlay line
    //   - For straight bets: max price across all bookOdds entries
    //   - Fallback: raw.price / raw.american_price (legacy fields)
    const getBestPrice = (item) => {
        const raw = item?.raw || item;
        // SGP path
        const sgpPrice = item?.combined_price ?? raw?.combined_price;
        if (sgpPrice != null && !isNaN(sgpPrice)) return sgpPrice;
        // Straight-bet path: best across all bookOdds
        const bookOdds = item?.bookOdds || raw?.bookOdds || {};
        const prices = Object.values(bookOdds)
            .map(o => {
                if (typeof o === 'number') return o;
                if (o && typeof o === 'object') return o.price ?? null;
                return null;
            })
            .filter(p => p !== null && !isNaN(p));
        if (prices.length > 0) return Math.max(...prices);
        // Legacy / fallback fields
        return raw?.price ?? raw?.american_price ?? raw?.odds ?? null;
    };

    // My Books-aware price helper: when My Books mode is active, only considers
    // prices from the user's sportsbooks. Falls back to global best if the item
    // has no My Books coverage (so it still sorts — just lower).
    // A single focused book (named in the query or the only sportsbook chip) is the
    // price the card shows, so it wins over My Books / global best.
    const _focusBookNorm = normBookName(
        preferredSportsbook
        || (Array.isArray(filterSportsbooks) && filterSportsbooks.length === 1 ? filterSportsbooks[0] : '')
    );
    const getEffectivePrice = (item) => {
        if (_focusBookNorm) {
            const raw = item?.raw || item;
            const sgpPrice = item?.combined_price ?? raw?.combined_price;
            if (sgpPrice != null && !isNaN(sgpPrice)) return sgpPrice;
            const bookOdds = raw?.bookOdds || item?.bookOdds || {};
            const key = Object.keys(bookOdds).find((bk) => normBookName(bk) === _focusBookNorm);
            const o = key ? bookOdds[key] : null;
            const p = typeof o === 'number' ? o : o?.price;
            if (p != null && !isNaN(p)) return p;
        }
        if (_wantsMyBooksMode && _myBooksList.length > 0) {
            const raw = item?.raw || item;
            const sgpPrice = item?.combined_price ?? raw?.combined_price;
            if (sgpPrice != null && !isNaN(sgpPrice)) return sgpPrice;
            const bookOdds = raw?.bookOdds || item?.bookOdds || {};
            const mbNorm = new Set(_myBooksList.map(b => normBookName(b)));
            const prices = Object.entries(bookOdds)
                .filter(([bk]) => mbNorm.has(normBookName(bk)))
                .map(([, o]) => {
                    if (typeof o === 'number') return o;
                    if (o && typeof o === 'object') return o.price ?? null;
                    return null;
                })
                .filter(p => p !== null && !isNaN(p));
            if (prices.length > 0) return Math.max(...prices);
            // No My Books coverage — fall back to global best so item still ranks
            return getBestPrice(item);
        }
        return getBestPrice(item);
    };

    // ⭐ Shared helper: Market type priority (pure > combo > either)
    // Used across all sort methods to ensure pure markets always appear before combo/either
    const getMarketTypePriorityShared = (option) => {
        const market = (option?.raw?.market || option?.raw?.market_id || option?.title || '').toLowerCase();
        if (market.includes('(combo)') || market.includes('+ ') || (market.includes('+') && market.includes('player'))) {
            return 500; // Combo markets
        }
        if (market.includes('(either)')) {
            return 200; // Either markets
        }
        if (market.includes('player ') && !market.includes('(')) {
            return 1000; // Pure player prop markets
        }
        return 800; // Other markets
    };
    
    switch (sortBy) {
        case 'relevance': {
            // Trust backend order, but apply two minimal overrides:
            // 1. Queried team first — e.g. "Devils 1p ML" shows New Jersey Devils before
            //    Florida Panthers (the opponent), not by relevance score but by team match.
            // 2. Earlier game date first — today's fixture before tomorrow's for same team.
            const qTeam = responseMetadata?._queryClassification?.entities?.team;
            const qTeamName = (qTeam?.name || '').toLowerCase();
            if (qTeamName) {
                const nameParts = qTeamName.split(/\s+/);
                const nickname = nameParts[nameParts.length - 1]; // e.g. "devils"
                const city = nameParts.slice(0, -1).join(' ');    // e.g. "new jersey"
                sorted.sort((a, b) => {
                    const aText = `${a.description || ''} ${a.raw?.selection || ''} ${a.raw?.name || ''}`.toLowerCase();
                    const bText = `${b.description || ''} ${b.raw?.selection || ''} ${b.raw?.name || ''}`.toLowerCase();
                    const aMatch = (nickname && aText.includes(nickname)) || (city && aText.includes(city));
                    const bMatch = (nickname && bText.includes(nickname)) || (city && bText.includes(city));
                    if (aMatch !== bMatch) return aMatch ? -1 : 1;
                    // Earlier game date first (ISO strings sort lexicographically)
                    const aDate = a.raw?.commence_time || a.raw?.event_date || a.raw?.start_date || a.date || '';
                    const bDate = b.raw?.commence_time || b.raw?.event_date || b.raw?.start_date || b.date || '';
                    if (aDate && bDate && aDate !== bDate) return aDate < bDate ? -1 : 1;
                    return 0; // stable: keep backend order for ties
                });
            }
            break;
        }
        case 'price_asc':
            sorted.sort((a, b) => {
                const priceA = getEffectivePrice(a) ?? -Infinity;
                const priceB = getEffectivePrice(b) ?? -Infinity;
                if (priceA !== priceB) return priceA - priceB;
                const filterA = getFilterRelevanceScore(a);
                const filterB = getFilterRelevanceScore(b);
                if (filterA !== filterB) return filterA - filterB;
                const relevanceA = getQueryRelevanceScore(a);
                const relevanceB = getQueryRelevanceScore(b);
                return relevanceB - relevanceA;
            });
            break;
        case 'odds': // NewResultsView "Best Odds" — same axis as old panel's price_desc
        case 'price_desc':
            sorted.sort((a, b) => {
                const priceA = getEffectivePrice(a) ?? -Infinity;
                const priceB = getEffectivePrice(b) ?? -Infinity;
                if (priceB !== priceA) return priceB - priceA;
                const filterA = getFilterRelevanceScore(a);
                const filterB = getFilterRelevanceScore(b);
                if (filterA !== filterB) return filterA - filterB;
                const relevanceA = getQueryRelevanceScore(a);
                const relevanceB = getQueryRelevanceScore(b);
                return relevanceB - relevanceA;
            });
            break;
        case 'ev': {
            // One canonical key per row: same math as UnifiedBetCard badge (devigMethods → min).
            // Peers map built from this filtered list so opposite-side devig matches the card.
            const peersMap = buildMarketPeersMap(filtered);
            const _getBookCount = (option) =>
                option?.availableBooks?.length ||
                (option?.bookOdds ? Object.keys(option.bookOdds).length : 0) ||
                option?.raw?._sportsbook_count || 1;

            // ⭐ Sort key must match badge EV exactly.
            // Grouped cards (backendAltLines) render with defaultSelectedBook=preferredSportsbook only.
            // Non-grouped cards render with defaultSelectedBook=option._selectedSportsbook||preferredSportsbook.
            // Using _selectedSportsbook for grouped options diverges from the badge's book lookup.

            // Helper: find best My Books book for an option (uses component-level _getMyBooksBest).
            // Pass `raw` so EV-based selection works (not just price-based).
            const _getBestMyBooksBook = (option) => {
                const bo = option?.raw?.bookOdds || option?.bookOdds || {};
                const raw = option?.raw || option || null;
                return _getMyBooksBest(bo, raw);
            };

            const _getEvForSort = (option) => {
                // ⭐ v1.43.24: Use `option?.raw || option` as fallback — matches _liveEv's
                // pattern. Previously `option?.raw || {}` caused samebook items where
                // _ev_source lives on the option directly (not in .raw) to miss the samebook
                // guard and fall through to getCanonicalSortEv, producing phantom high EVs that
                // placed 0-1% samebook bets between 10-20% non-samebook results in the sorted list.
                const raw = option?.raw || option || {};
                const isSGP = raw.ev_method === 'individual_leg_devig';
                const bookOdds = raw.bookOdds || option?.bookOdds || {};
                const isGroupedOption = !!(raw.alternateLines?.length || option?.alternateLines?.length);
                const bookToUse = isGroupedOption
                    ? preferredSportsbook
                    : (option?._selectedSportsbook || preferredSportsbook);
                // For samebook devig, per-book consensus computation produces phantom EV
                // when bookData.clean_ev_percent is not cached per-book. Trust the
                // server-stamped top-level EV so these items sort at their true EV.
                const _rawEvSrc = raw._ev_source || raw.devig_methods?.ev_source;
                if (_rawEvSrc === 'samebook') {
                    const _isMbSort = _wantsMyBooksMode || userProfile?.preferences?.defaultBookSort === 'mybooks';
                    const serverEv = _isMbSort
                        ? (raw.ev_percent_mybooks ?? raw.ev_percent_clean ?? raw.ev_percent ?? null)
                        : (raw.ev_percent_clean ?? raw.ev_percent ?? null);
                    if (Number.isFinite(serverEv)) return serverEv;
                }
                // When filtered to a specific book, sort by THAT book's clean EV
                // (consensus × decimal — matches what the card displays).
                let ev = null;
                if (bookToUse && bookToUse !== 'best') {
                    const wc = cleanPerBookEv(raw, bookOdds[bookToUse], isSGP, consensusOptions, bookToUse);
                    if (Number.isFinite(wc)) ev = wc;
                }
                const _isMbMode = _wantsMyBooksMode || sortBy === 'mybooks';
                if (ev == null) {
                    // ⭐ v1.18 P0 — ONE SORT AXIS. Compute the sort EV with the SAME
                    // canonical per-book walk the card pill uses (cleanPerBookEv over
                    // the relevant book list, take the max). The card auto-selects its
                    // top perBookEV book, so pill ≡ max(cleanPerBookEv) by construction.
                    //
                    // Previously this preferred the server-stamped ev_percent_clean /
                    // ev_percent_mybooks (annotateCleanEv). For SGPs that stamp is
                    // computed WITHOUT leave-one-out R while cleanPerBookEv recomputes
                    // WITH it — two competing numbers, so the list order could disagree
                    // with the pill. Server stamps are now fallback-only (below), used
                    // when the canonical walk can't produce a number (legacy cache
                    // entries without method stamps / bookOdds).
                    //
                    // Note: cleanPerBookEv Priority 0 still reads the backend's
                    // per-book clean_ev_percent stamp when present — backend stays
                    // authoritative where it stamped per-book values; only the
                    // top-level aggregate stamps lost their priority.
                    if (_isMbMode) {
                        // My Books mode: pill = max cleanPerBookEv over the user's books
                        // (card auto-selects top My Books book).
                        const myBooksBook = _getBestMyBooksBook(option);
                        if (myBooksBook) {
                            const wc = cleanPerBookEv(raw, bookOdds[myBooksBook], isSGP, consensusOptions, myBooksBook);
                            if (Number.isFinite(wc)) ev = wc;
                        }
                    } else {
                        // All Books mode: max canonical EV across every book on the card.
                        let best = null;
                        for (const [bk, bd] of Object.entries(bookOdds)) {
                            const wc = cleanPerBookEv(raw, bd, isSGP, consensusOptions, bk);
                            if (Number.isFinite(wc) && (best == null || wc > best)) best = wc;
                        }
                        if (best != null) ev = best;
                    }
                }
                if (ev == null) {
                    // Fallback: server-stamped aggregate EV (annotateCleanEv) for rows
                    // the canonical walk can't price (no bookOdds / no method stamps).
                    const _serverEv = _isMbMode
                        ? (raw.ev_percent_mybooks ?? raw.ev_percent_clean ?? null)
                        : (raw.ev_percent_clean ?? null);
                    if (Number.isFinite(_serverEv)) ev = _serverEv;
                }
                if (ev == null) ev = getCanonicalSortEv(option, peersMap, consensusOptions);
                return ev;
            };

            const _evSort = (a, b) => {
                const evA = _getEvForSort(a);
                const evB = _getEvForSort(b);
                if (evA !== evB) return evB - evA;
                const booksA = _getBookCount(a);
                const booksB = _getBookCount(b);
                if (booksA !== booksB) return booksB - booksA;
                try {
                    const dateA = new Date(a?.raw?.start_date || a?.raw?.start_time || a?.raw?.commence_time || 0);
                    const dateB = new Date(b?.raw?.start_date || b?.raw?.start_time || b?.raw?.commence_time || 0);
                    return dateA.getTime() - dateB.getTime();
                } catch (e) { return 0; }
            };

            // ⭐ My Books Priority: tiered sort when preference is on.
            // Delegates to sortByMyBooksPriority (shared util) so logic stays in sync.
            // Passes _evSort as the within-tier comparator so EV ranking matches the card badges.
            const _mbList = userProfile?.preferences?.myBooks || [];
            // Use My Books priority when either the stored preference OR the live bottom
            // toggle says My Books mode is active. This ensures "Best EV" sorts by My Books
            // EV when the user has the "My Books" toggle selected at the bottom.
            const _mbPriority = (_wantsMyBooksMode || userProfile?.preferences?.defaultBookSort === 'mybooks') && !disableMyBooksPriority;
            if (_mbPriority && _mbList.length > 0) {
                const _mbSorted = sortByMyBooksPriority(sorted, { myBooks: _mbList }, _evSort);
                sorted.splice(0, sorted.length, ..._mbSorted);
            } else {
                sorted.sort(_evSort);
            }
            break;
        }
        case 'date':
            // ⭐ USER-SELECTED SORT: Prioritize the selected criterion FIRST
            sorted.sort((a, b) => {
                // FIRST: Sort by date (earlier first) - user's explicit choice
                try {
                    const dateA = new Date(a?.raw?.start_date || a?.raw?.start_time || a?.raw?.commence_time || 0);
                    const dateB = new Date(b?.raw?.start_date || b?.raw?.start_time || b?.raw?.commence_time || 0);
                    if (dateA.getTime() !== dateB.getTime()) return dateA - dateB;
                } catch (e) {
                    // Continue to tiebreakers
                }
                
                // TIEBREAKER: Filter relevance
                const filterA = getFilterRelevanceScore(a);
                const filterB = getFilterRelevanceScore(b);
                if (filterA !== filterB) return filterA - filterB;
                
                // TIEBREAKER: Query relevance
                const relevanceA = getQueryRelevanceScore(a);
                const relevanceB = getQueryRelevanceScore(b);
                return relevanceB - relevanceA;
            });
            break;
        case 'sportsbooks':
            // Same traditional-book count as the min-books quality floor so
            // Kalshi/offshore keys cannot outrank a real 15-book market.
            sorted.sort((a, b) => {
                const countBooks = _getTradBookCount;
                const countA = countBooks(a);
                const countB = countBooks(b);
                if (countB !== countA) return countB - countA;
                const evOf = (option) => {
                    const raw = option?.raw || option || {};
                    const isSGP = raw.ev_method === 'individual_leg_devig';
                    const bo = raw.bookOdds || option?.bookOdds || {};
                    let best = null;
                    for (const [bk, bd] of Object.entries(bo)) {
                        const wc = cleanPerBookEv(raw, bd, isSGP, consensusOptions, bk);
                        if (Number.isFinite(wc) && (best == null || wc > best)) best = wc;
                    }
                    return best ?? -Infinity;
                };
                const evA = evOf(a);
                const evB = evOf(b);
                if (evA !== evB) return evB - evA;
                const relevanceA = getQueryRelevanceScore(a);
                const relevanceB = getQueryRelevanceScore(b);
                return relevanceB - relevanceA;
            });
            break;
        case 'mybooks': {
            // ⭐ MY BOOKS SORT: primary = best clean EV achievable INSIDE the user's
            // books (so the top card is the highest-EV bet one of their books carries).
            // Items no My Books carry sink to the bottom. Coverage count is a tiebreaker.
            const _myBooksChip = userProfile?.preferences?.myBooks || [];
            const _myBooksNorm = new Set(_myBooksChip.map(b => normBookName(b)));

            const bestEvInMyBooks = (option) => {
                const raw = option?.raw || {};
                const isSGP = raw.ev_method === 'individual_leg_devig';
                const bookOdds = raw.bookOdds || option?.bookOdds || {};
                let best = null;
                for (const [bk, bd] of Object.entries(bookOdds)) {
                    if (!_myBooksNorm.has(normBookName(bk))) continue;
                    const wc = cleanPerBookEv(raw, bd, isSGP, consensusOptions, bk);
                    if (wc != null && (best == null || wc > best)) best = wc;
                }
                return best;
            };

            sorted.sort((a, b) => {
                const evA = bestEvInMyBooks(a);
                const evB = bestEvInMyBooks(b);
                // Items My Books don't carry → bottom. Do NOT fall back to
                // global-books EV here — that's what lets a bet with no My
                // Books coverage but a great global price rank above a real
                // My Books bet with a modest EV. Preserve upstream order
                // (stable sort) for uncovered items; they're deprioritized.
                if (evA == null && evB == null) return 0;
                if (evA == null) return 1;
                if (evB == null) return -1;
                if (evA !== evB) return evB - evA;
                // Tiebreaker: coverage (more My Books carrying the bet).
                // No further global-EV tiebreaker — My Books is the only axis.
                const scoreA = scoreMyBooksOverlap(a, _myBooksChip);
                const scoreB = scoreMyBooksOverlap(b, _myBooksChip);
                if (Math.abs(scoreB - scoreA) > 0.001) return scoreB - scoreA;
                return 0;
            });
            break;
        }
        case 'market':
            // ⭐ USER-SELECTED SORT: Prioritize the selected criterion FIRST
            sorted.sort((a, b) => {
                // FIRST: Sort by market name alphabetically - user's explicit choice
                const marketA = (a?.raw?.market || a?.title || '').toLowerCase();
                const marketB = (b?.raw?.market || b?.title || '').toLowerCase();
                const marketCompare = marketA.localeCompare(marketB);
                if (marketCompare !== 0) return marketCompare;
                
                // TIEBREAKER: Filter relevance
                const filterA = getFilterRelevanceScore(a);
                const filterB = getFilterRelevanceScore(b);
                if (filterA !== filterB) return filterA - filterB;
                
                // TIEBREAKER: Query relevance
                const relevanceA = getQueryRelevanceScore(a);
                const relevanceB = getQueryRelevanceScore(b);
                return relevanceB - relevanceA;
            });
            break;
    }
    
    try {
        Object.defineProperty(sorted, 'filterStageStats', {
            value: _stageCounts,
            enumerable: false,
        });
        Object.defineProperty(sorted, 'lineTypeCounts', {
            value: lineTypeCounts,
            enumerable: false,
        });
        Object.defineProperty(sorted, 'unreliableOnlyBooks', {
            value: _unreliableOnlyBooks,
            enumerable: false,
        });
    } catch { /* no-op */ }
    return sorted;
}
}

export function useFilteredAndSortedOptions(d) {
    const {
        fetchedOptions, myBooksFetchedOptions, resultsContextQuery, responseMetadata,
        referenceTime, didBroaden, preferredSportsbook,
        searchText, filterSportsbooks, filterMarkets, filterPlayers, filterResultType,
        filterDeepLinksOnly, filterPositiveEvOnly, filterLineType, filterLegCount,
        disableMinBooksFilter, disableDateKeywordFilter, disableRelevanceFilter,
        sortBy, _effectiveBooksSort, _wantsMyBooksMode, userProfile,
    } = d;
    const hideNoEvRows = useHideNoEvRows();
    // _effectiveBooksSort must be included so the memo re-runs when the user
    // toggles All Books ↔ My Books (booksViewOverride changes). Without it,
    // _wantsMyBooksMode and _useMyBooksSet read stale values and the sort
    // stays locked to whichever mode was active when the memo last ran.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => computeFilteredAndSortedOptions({ ...d, hideNoEvRows }), [hideNoEvRows, fetchedOptions, myBooksFetchedOptions, searchText, filterSportsbooks, filterMarkets, filterPlayers, filterResultType, filterDeepLinksOnly, filterPositiveEvOnly, filterLineType, filterLegCount, sortBy, resultsContextQuery, didBroaden, disableMinBooksFilter, disableDateKeywordFilter, disableRelevanceFilter, preferredSportsbook, userProfile?.preferences?.minBooksStraights, userProfile?.preferences?.minBooksSGP, userProfile?.preferences?.minBooksMyBooks, userProfile?.preferences?.myBooks, userProfile?.preferences?.defaultBookSort, _effectiveBooksSort, _wantsMyBooksMode, responseMetadata, referenceTime]);
}
